import React from "react";
import "../vaultMigration.css";
import { 
  FiArrowLeft, 
  FiDatabase, 
  FiCpu, 
  FiRefreshCw, 
  FiUsers, 
  FiTrendingUp, 
  FiExternalLink, 
  FiPlay, 
  FiCheckCircle, 
  FiArrowRight 
} from "react-icons/fi";

function VaultCRMPage({ onBackToHome, onStartMigration }) {
  const VEEVA_DOCS_URL = "https://vaultcrmhelp.veeva.com/doc/Content/CRM_topics/Getting_Started/TransitionToVault.htm";

  const keyFeatures = [
    { 
      icon: FiCpu, 
      title: "AI-Powered Migration", 
      description: "Intelligent automation that analyzes, maps, and transforms your data seamlessly" 
    },
    { 
      icon: FiRefreshCw, 
      title: "Seamless Transition", 
      description: "Zero-downtime migration with real-time sync between legacy and new systems" 
    },
    { 
      icon: FiUsers, 
      title: "Improved Adoption", 
      description: "Built-in training modules and intuitive interfaces for faster team onboarding" 
    },
    { 
      icon: FiTrendingUp, 
      title: "Platform Optimization", 
      description: "Automated performance tuning and best practice implementation" 
    }
  ];

  const migrationSteps = [
    { step: 1, title: "Mapping", description: "Define field mappings" },
    { step: 2, title: "Documents", description: "Select and upload files" },
    { step: 3, title: "Process", description: "Generate Vault AI-enhanced content" },
    { step: 4, title: "Vocabulary", description: "Apply terminology enhancement" },
    { step: 5, title: "Compare", description: "Review Veeva vs Vault side-by-side" },
    { step: 6, title: "Download", description: "Export final migrated document" }
  ];

  const handleViewDocumentation = () => {
    window.open(VEEVA_DOCS_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="vault-crm-page">
      <div className="vault-crm-bg">
        <div className="bg-gradient-1"></div>
        <div className="bg-gradient-2"></div>
        <div className="bg-grid"></div>
      </div>
      
      <button className="back-button" onClick={onBackToHome}>
        <FiArrowLeft className="back-icon" />
        <span>Back to Home</span>
      </button>
      
      <section className="vault-hero">
        <div className="vault-hero-inner">
          <div className="vault-hero-content">
            <div className="vault-hero-badge">
              <FiCpu className="badge-icon" />
              <span>AI-Powered Solution</span>
            </div>
            <h1 className="vault-hero-title">
              Vault CRM <span className="highlight">Migration</span> Tool
            </h1>
            <p className="vault-hero-subtitle">
              Seamlessly migrate from Veeva CRM to Vault CRM with our intelligent AI-powered platform. 
              Optimize your workflows and accelerate user adoption with guided migration assistance.
            </p>
            <div className="vault-hero-cta">
              <button className="cta-primary" onClick={onStartMigration}>
                <FiPlay className="cta-icon" />
                Start Migration
              </button>
              <button className="cta-secondary" onClick={handleViewDocumentation}>
                <FiExternalLink className="cta-icon" />
                View Documentation
              </button>
            </div>
          </div>
          
          <div className="vault-hero-visual">
            <div className="migration-visual">
              <div className="visual-source">
                <div className="visual-icon-box source">
                  <span className="visual-label">Veeva CRM</span>
                  <FiDatabase className="visual-icon" />
                </div>
              </div>
              <div className="visual-arrow">
                <div className="arrow-line">
                  <div className="arrow-pulse"></div>
                </div>
                <div className="ai-badge">
                  <FiCpu className="ai-icon" />
                  <span>AI</span>
                </div>
              </div>
              <div className="visual-target">
                <div className="visual-icon-box target">
                  <span className="visual-label">Vault CRM</span>
                  <FiDatabase className="visual-icon" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="vault-section features-section">
        <div className="section-container">
          <div className="section-header-center">
            <h2 className="section-title-large">Key Capabilities</h2>
            <p className="section-subtitle-large">
              Everything you need for a successful CRM transformation
            </p>
          </div>
          <div className="features-grid-4">
            {keyFeatures.map((feature, index) => {
              const FeatureIcon = feature.icon;
              return (
                <div key={index} className="feature-card-large">
                  <div className="feature-icon-large">
                    <FeatureIcon />
                  </div>
                  <h3 className="feature-title-large">{feature.title}</h3>
                  <p className="feature-desc-large">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="vault-section process-section">
        <div className="section-container">
          <div className="section-header-center">
            <h2 className="section-title-large">Migration Process</h2>
            <p className="section-subtitle-large">
              A structured, AI-guided approach to ensure successful transformation
            </p>
          </div>
          <div className="process-steps">
            {migrationSteps.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-indicator">
                  <div className="step-number">{step.step}</div>
                  {index < migrationSteps.length - 1 && (
                    <div className="step-connector">
                      <FiArrowRight className="connector-icon" />
                    </div>
                  )}
                </div>
                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="process-cta">
            <div className="process-cta-content">
              <FiCheckCircle className="cta-check-icon" />
              <div>
                <h3>Ready to Begin Your Transformation?</h3>
                <p>Our AI-powered tool will guide you through every step of the migration process</p>
              </div>
            </div>
            <button className="cta-primary" onClick={onStartMigration}>
              <FiPlay className="cta-icon" />
              Start Migration
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default VaultCRMPage;